// Role-driven staff/student create+edit form (Step 5e Phase 7a, decision 10). One form: the
// role selector reveals student training fields. On create the server issues a one-time temp
// password which is shown once (no email infra yet). Edit never touches the password.

import { useEffect, useState, type CSSProperties } from 'react';
import { C, F, Modal, Btn, ErrorText } from './adminUi';
import { createUser, updateUser, getUser, type DuplicateMatch } from '../../lib/admin';
import { buildUserFormBody, type UserFormValues } from '../../lib/admin/userFormBody';
import { identityPrompts } from '../../lib/admin/identityPrompts';

// Every current SystemRoleType is selectable (incl. the escalation add-on roles
// senior_base_pilot/ops_manager and haamc). A user may hold MULTIPLE roles — the backend
// accepts a roles[] array (admin-users.schemas rolesField .min(1)) and syncUserRoles sets the
// full set — so this is a multi-select.
const ROLE_OPTIONS = ['instructor', 'ops_controller', 'pilot', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager', 'student', 'client'];
const ROLE_LABELS: Record<string, string> = {
  instructor: 'Instructor', ops_controller: 'Ops Controller', pilot: 'Pilot', haamc: 'HAAMC', admin: 'Admin',
  senior_base_pilot: 'Senior Base Pilot / Base Manager', ops_manager: 'Operations Manager', student: 'Student', client: 'Client',
};
// Staff roles drive the "ask first" / PPR fields. student/client are NOT staff.
const STAFF_ROLE_SET = new Set(['instructor', 'ops_controller', 'pilot', 'haamc', 'admin', 'senior_base_pilot', 'ops_manager']);
const inp: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, width: '100%' };
const lbl: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, display: 'block', marginBottom: 3 };

// Legacy full_name → best-effort first/last for the edit form. Structured names win when present;
// otherwise split on the LAST whitespace token (last token → last name, remainder → first name); a
// single token → first name only (last blank, which the Save validation then requires filling).
// NOTE: the caller only runs this for PERSON records — a company-only client (full_name = company)
// is left blank so the company name isn't mangled into person-name fields.
function splitLegacyName(u: { first_name: string | null; last_name: string | null; full_name: string }): { first: string; last: string } {
  if (u.first_name || u.last_name) return { first: u.first_name ?? '', last: u.last_name ?? '' };
  const parts = (u.full_name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: parts[0] ?? '', last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

export default function UserFormModal({ mode, userId, baseOptions, onClose, onSaved }: {
  mode: 'create' | 'edit';
  userId?: string;
  baseOptions: { value: string; label: string }[];
  onClose: () => void;
  // `created` (when present, on create) lets a caller add the new user immediately; the admin
  // list ignores it. Optional arg keeps the existing `() => …` callers valid.
  onSaved: (created?: { id: string; label: string }) => void;
}) {
  const [v, setV] = useState<Record<string, string | boolean>>({ arn_pending: false, approval_required: false });
  // No default role — the operator must consciously pick (a blank set disables Create). Previously
  // this defaulted to ['instructor'], which silently created students/clients as instructors.
  const [roles, setRolesState] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState<string | null>(null);
  const [dupes, setDupes] = useState<DuplicateMatch[] | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);
  // Person/organisation entry prompts (COMPANY-SETTINGS-ADMIN §7). ⚠ ADVISORY ONLY — held in
  // their own state and cleared by confirming, exactly like the duplicate prompt above. They
  // never contribute a reason to refuse; the record is written exactly as typed.
  const [identityWarnings, setIdentityWarnings] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(mode === 'edit');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (mode !== 'edit' || !userId) return;
    let live = true;
    getUser(userId).then((r) => {
      if (!live) return;
      if (r.error || !r.data) { setError(r.error ?? 'Failed to load.'); setLoading(false); return; }
      const u = r.data.user;
      // A company-only client (company set, no structured person name) keeps First/Last blank — the
      // company prefills its own input below; don't split full_name (= company) into person-name
      // fields. All other rows prefill/split normally.
      const companyOnly = !!u.company && !u.first_name && !u.last_name;
      const { first, last } = companyOnly ? { first: '', last: '' } : splitLegacyName(u);
      setV({
        full_name: u.full_name, first_name: first, last_name: last, company: u.company ?? '',
        email: u.email, preferred_name: u.preferred_name ?? '', mobile: u.mobile ?? '',
        pilot_code: u.pilot_code ?? '', home_base_id: u.home_base_id ?? '',
        training_stage: u.training_stage ?? '', arn: u.arn ?? '', arn_pending: u.arn_pending,
        approval_required: u.approval_required ?? false, approval_note: u.approval_note ?? '',
        ui_color: u.ui_color ?? '', // PR-000059 — blank means 'derived', and blank is what clears it
      });
      // Initialise from ALL roles (not roles[0]) so saving a multi-role user preserves every role.
      setRolesState(u.roles ?? []);
      setLoading(false);
    });
    return () => { live = false; };
  }, [mode, userId]);

  // Mark dirty on user edits only (the async load above uses setV directly, so it won't trip
  // this) — a stray backdrop click / Esc then prompts before discarding.
  const set = (k: string, val: string | boolean) => { setTouched(true); setV((s) => ({ ...s, [k]: val })); };
  const str = (k: string) => (typeof v[k] === 'string' ? (v[k] as string) : '');
  const toggleRole = (r: string) => {
    setTouched(true);
    setRolesState((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  };
  const isStudent = roles.includes('student');
  const isClient = roles.includes('client');
  // Staff "ask first" / PPR applies to staff roles only — never student/client (Item 3).
  const isStaff = roles.some((r) => STAFF_ROLE_SET.has(r));
  // Allowed combo (the backend supports holding a staff role alongside student/client) — surfaced
  // with a gentle note rather than blocked, so e.g. an instructor who is also enrolled as a student
  // keeps both sets of fields. (Choice: allow + note, NOT mutually exclusive.)
  const mixedStaffAndLearner = isStaff && (isStudent || isClient);

  async function submit(confirmNew = false, confirmIdentity = false) {
    setError(null); setDupes(null);
    if (roles.length === 0) { setError('Select at least one role.'); return; }
    const first = str('first_name').trim();
    const last = str('last_name').trim();
    const company = str('company').trim();
    // Identity rule (matches the ☰ New student/client modal): (first AND last) OR company.
    if (!((first && last) || company)) { setError('Enter a first and last name, or a company.'); return; }

    // ⚠ WARN, NEVER BLOCK. Shown once, before the first save attempt; confirming proceeds with
    // the values UNCHANGED — nothing here rewrites a field or refuses one. A sole trader with a
    // company name is a real person who may be a training student, so "has a company name" is
    // fine as an entry-time prompt and must never become a gate. It is emphatically NOT the
    // discriminator for anything with legal weight (the student-module terms exemption keys off
    // the person's system role).
    if (!confirmIdentity) {
      const prompts = identityPrompts({ first_name: first, last_name: last, company, roles });
      if (prompts.length) { setIdentityWarnings(prompts); return; }
    }
    setIdentityWarnings(null);
    setBusy(true);
    // The payload rules live in buildUserFormBody (lib/admin/userFormBody.ts), extracted 2026-08-27
    // so create-versus-edit behaviour is VERIFIABLE. The clearable-field defect it fixes was
    // invisible to any service-level test, because the service has always honoured an explicit
    // null — only the form failed to send one.
    const body = buildUserFormBody({ mode, values: v as UserFormValues, roles, isStudent, isStaff, confirmNew });
    const res = mode === 'create' ? await createUser(body) : await updateUser(userId!, body);
    setBusy(false);
    const dup = (res as { duplicates?: DuplicateMatch[] }).duplicates;
    if (mode === 'create' && dup && dup.length) { setDupes(dup); return; } // advisory — confirm
    if (res.error) { setError(res.error); return; }
    if (mode === 'create' && res.data) {
      const created = res.data as unknown as { user: { id: string }; temp_password: string };
      setCreatedId(created.user.id);
      setTempPw(created.temp_password);
      return;
    }
    onSaved();
  }
  const done = () => {
    const first = str('first_name').trim(), last = str('last_name').trim(), company = str('company').trim();
    const label = (first && last ? `${first} ${last}` : company) || str('email');
    onSaved(createdId ? { id: createdId, label } : undefined);
  };

  if (tempPw) {
    return (
      <Modal title="User created" onClose={done} width={460}>
        <p style={{ fontSize: 13, color: C.textDim }}>The account is created. Give this <b>one-time temporary password</b> to the user out-of-band (no email is sent yet). They should change it on first login.</p>
        <div style={{ fontFamily: 'monospace', fontSize: 16, background: C.surfaceAlt, border: `1px solid ${C.borderMed}`, borderRadius: 8, padding: '12px 14px', textAlign: 'center', letterSpacing: 1, margin: '12px 0' }}>{tempPw}</div>
        <Btn kind="primary" onClick={done}>Done</Btn>
      </Modal>
    );
  }

  // Advisory identity prompt — the shape of the duplicate panel below, and equally non-blocking.
  if (identityWarnings) {
    return (
      <Modal title="Check this record" onClose={() => setIdentityWarnings(null)} width={460}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 0 10px' }}>
          {identityWarnings.map((w, i) => (
            <div key={i} style={{ fontSize: 13, color: C.text, background: C.surfaceAlt, border: `1px solid ${C.borderMed}`, borderRadius: 8, padding: '10px 12px' }}>{w}</div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <Btn kind="primary" disabled={busy} onClick={() => submit(false, true)}>{busy ? 'Saving…' : 'Save it as entered'}</Btn>
          <Btn kind="ghost" onClick={() => setIdentityWarnings(null)}>Go back and change it</Btn>
        </div>
      </Modal>
    );
  }

  if (dupes) {
    return (
      <Modal title="Possible duplicate" onClose={() => setDupes(null)} width={460}>
        <p style={{ fontSize: 13, color: C.textDim }}>A user of this name already exists — is this the same person?</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '10px 0' }}>
          {dupes.map((d) => (
            <div key={d.id} style={{ fontSize: 13, background: C.surfaceAlt, border: `1px solid ${C.borderMed}`, borderRadius: 8, padding: '8px 10px' }}>{d.label}</div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          <Btn kind="primary" disabled={busy} onClick={() => submit(true, true)}>{busy ? 'Creating…' : 'Create as a new record'}</Btn>
          <Btn kind="ghost" onClick={() => setDupes(null)}>Back</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={mode === 'create' ? 'New staff / student' : 'Edit user'} onClose={onClose} width={560} dirty={touched}>
      {loading ? <div style={{ color: C.textMuted, padding: 16 }}>Loading…</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Roles * <span style={{ fontWeight: 400, color: C.textMuted }}>(select one or more)</span></label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', padding: '4px 0' }}>
              {ROLE_OPTIONS.map((r) => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: C.textDim, cursor: 'pointer' }}>
                  <input type="checkbox" checked={roles.includes(r)} onChange={() => toggleRole(r)} style={{ width: 15, height: 15, accentColor: C.accent }} />
                  {ROLE_LABELS[r] ?? r}
                </label>
              ))}
            </div>
            {roles.length === 0 && <div style={{ fontSize: 11.5, color: C.red, marginTop: 2 }}>Select at least one role.</div>}
            {mixedStaffAndLearner && <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 2 }}>This user has both a staff role and a student/client role — both sets of fields below apply.</div>}
          </div>
          <div><label style={lbl}>First name</label><input style={inp} value={str('first_name')} onChange={(e) => set('first_name', e.target.value)} /></div>
          <div><label style={lbl}>Last name</label><input style={inp} value={str('last_name')} onChange={(e) => set('last_name', e.target.value)} /></div>
          <div><label style={lbl}>Preferred name</label><input style={inp} value={str('preferred_name')} onChange={(e) => set('preferred_name', e.target.value)} /></div>
          <div><label style={lbl}>Staff ID / Name code</label><input style={inp} value={str('pilot_code')} onChange={(e) => set('pilot_code', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Company</label><input style={inp} value={str('company')} onChange={(e) => set('company', e.target.value)} placeholder="or a first and last name above" /></div>
          <div style={{ gridColumn: '1 / -1', fontSize: 11.5, color: C.textMuted, marginTop: -4 }}>Enter a first and last name, or a company.</div>
          <div><label style={lbl}>Email *</label><input style={inp} value={str('email')} onChange={(e) => set('email', e.target.value)} /></div>
          <div><label style={lbl}>Mobile</label><input style={inp} value={str('mobile')} onChange={(e) => set('mobile', e.target.value)} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Home base</label>
            <select value={str('home_base_id')} onChange={(e) => set('home_base_id', e.target.value)} style={inp}>
              <option value="">— none —</option>
              {baseOptions.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </div>
          {isStudent && <>
            <div><label style={lbl}>Training stage</label><input style={inp} value={str('training_stage')} onChange={(e) => set('training_stage', e.target.value)} /></div>
            <div><label style={lbl}>ARN</label><input style={inp} value={str('arn')} onChange={(e) => set('arn', e.target.value)} /></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={!!v.arn_pending} onChange={(e) => set('arn_pending', e.target.checked)} style={{ width: 16, height: 16, accentColor: C.accent }} /><span style={{ fontSize: 12.5, color: C.textDim }}>ARN pending</span></div>
          </>}
          {isStaff && <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={!!v.approval_required} onChange={(e) => set('approval_required', e.target.checked)} style={{ width: 16, height: 16, accentColor: C.accent }} /><span style={{ fontSize: 12.5, color: C.textDim }}>Approval required (ask first)</span></div>
            <div><label style={lbl}>Who to ask / reason</label><input style={inp} value={str('approval_note')} onChange={(e) => set('approval_note', e.target.value)} placeholder="optional" /></div>
            {/* PR-000059 — ops-board chip colour. Swatch + hex text, the BookingKindsManager
                pattern. ⚠ THE CLEAR BUTTON IS THE POINT, not a convenience: an empty box sends an
                explicit null (buildUserFormBody), which returns the chip to its DERIVED colour.
                A native <input type="color"> cannot express "no colour" — it always reports a
                value — so without this control the field would be unclearable, which is exactly
                the defect preferred_name / pilot_code / mobile had until 2026-08-27. */}
            <div>
              <label style={lbl}>Ops board colour</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="color"
                  value={/^#[0-9A-Fa-f]{6}$/.test(str('ui_color')) ? str('ui_color') : '#a59427'}
                  onChange={(e) => set('ui_color', e.target.value)}
                  style={{ width: 40, height: 30, border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', cursor: 'pointer', padding: 2 }}
                />
                <input
                  style={{ ...inp, width: 104, fontFamily: 'monospace' }}
                  value={str('ui_color')}
                  onChange={(e) => set('ui_color', e.target.value)}
                  placeholder="auto"
                />
                <Btn kind="ghost" onClick={() => set('ui_color', '')} disabled={!str('ui_color')}>Clear</Btn>
              </div>
              <div style={{ fontSize: 11.5, color: C.textMuted, marginTop: 3 }}>
                {str('ui_color') ? 'Assigned colour.' : 'Blank uses an automatic colour derived from this person.'}
              </div>
            </div>
          </>}
        </div>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      <div style={{ display: 'flex', gap: 10, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <Btn kind="primary" disabled={busy || loading || roles.length === 0} onClick={() => submit()}>{busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}</Btn>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}
